import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { TID } from "@/lib/testIds";
import { PRESTATIONS, IMAGES } from "@/lib/data";
import OptimizedImage from "@/components/OptimizedImage";
import { trackContactCta } from "@/lib/analytics";

const navItems = [
    { to: "/a-propos", label: "À propos", tid: TID.nav.about },
    // Prestations dropdown handled separately
    { to: "/entreprises", label: "Entreprises", tid: TID.nav.entreprises },
    { to: "/tarifs", label: "Tarifs", tid: TID.nav.tarifs },
];

const mobileNavItems = [
    ...navItems.slice(0, 1),
    {
        to: "/prestations",
        label: "Prestations",
        tid: "nav-mobile-prestations",
    },
    ...navItems.slice(1),
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [presta, setPresta] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setPresta(false);
    }, [location.pathname]);

    return (
        <header
            data-testid={TID.nav.root}
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-creme/85 backdrop-blur-xl border-b border-line/70"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                {/* Logo → Accueil */}
                <Link
                    to="/"
                    className="group flex items-center"
                    data-testid="site-logo"
                >
                    <OptimizedImage
                        src={IMAGES.logoBlack}
                        srcSet={IMAGES.logoBlackSrcSet}
                        sizes="(max-width: 768px) 112px, 128px"
                        alt="Joëlle Berthaud — Réflexologue"
                        className="h-14 md:h-16 w-auto transition-opacity duration-300 group-hover:opacity-70"
                        width={202}
                        height={91}
                        decoding="async"
                        fetchPriority="high"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    <NavLink
                        to="/a-propos"
                        data-testid={TID.nav.about}
                        className={({ isActive }) =>
                            `text-sm relative py-2 transition-colors ${
                                isActive
                                    ? "text-brown"
                                    : "text-brown-soft hover:text-brown"
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                À propos
                                {isActive && (
                                    <motion.span
                                        layoutId="nav-underline"
                                        className="absolute left-0 right-0 -bottom-0.5 h-px bg-terracotta"
                                    />
                                )}
                            </>
                        )}
                    </NavLink>

                    {/* Prestations dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setPresta(true)}
                        onMouseLeave={() => setPresta(false)}
                    >
                        <button
                            data-testid={TID.nav.prestations}
                            className="flex items-center gap-1 text-sm text-brown-soft hover:text-brown py-2"
                        >
                            Prestations
                            <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        <AnimatePresence>
                            {presta && (
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 4 }}
                                    transition={{ duration: 0.25 }}
                                    className="absolute top-full left-0 pt-4"
                                >
                                    <div className="bg-off-white border border-line rounded-md shadow-lg p-3 min-w-[280px]">
                                        <Link
                                            to="/prestations"
                                            className="block px-3 py-2 rounded text-xs uppercase tracking-widest text-brown-soft hover:bg-creme"
                                        >
                                            Toutes les prestations
                                        </Link>
                                        <div className="h-px bg-line my-1" />
                                        {PRESTATIONS.map((p) => (
                                            <Link
                                                key={p.slug}
                                                to={`/prestations/${p.slug}`}
                                                className="flex items-baseline gap-3 px-3 py-2.5 rounded hover:bg-creme transition-colors"
                                                data-testid={`nav-presta-${p.slug}`}
                                            >
                                                <span className="font-serif text-xs text-terracotta">
                                                    {p.num}
                                                </span>
                                                <div>
                                                    <div className="font-serif text-base text-brown leading-tight">
                                                        {p.name}
                                                    </div>
                                                    <div className="text-xs text-brown-soft">
                                                        {p.duration} · {p.price}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {navItems.slice(1).map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={n.tid}
                            className={({ isActive }) =>
                                `text-sm relative py-2 transition-colors ${
                                    isActive
                                        ? "text-brown"
                                        : "text-brown-soft hover:text-brown"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {n.label}
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-underline"
                                            className="absolute left-0 right-0 -bottom-0.5 h-px bg-terracotta"
                                        />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* CTA = Contact */}
                <div className="hidden lg:flex items-center gap-3">
                    <Link
                        to="/contact"
                        data-testid={TID.nav.book}
                        onClick={() => trackContactCta("header")}
                        className="group inline-flex items-center gap-2 bg-brown text-creme rounded-full pl-5 pr-1.5 py-1.5 text-sm transition-colors hover:bg-terracotta-deep"
                    >
                        Prendre rendez-vous
                        <span className="w-8 h-8 rounded-full bg-creme text-brown grid place-items-center transition-transform duration-500 group-hover:rotate-45">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path
                                    d="M2 10L10 2M10 2H4M10 2V8"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </span>
                    </Link>
                </div>

                <button
                    data-testid={TID.nav.mobileToggle}
                    onClick={() => setOpen((o) => !o)}
                    className="lg:hidden w-11 h-11 grid place-items-center text-brown"
                    aria-label="Menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden bg-creme border-t border-line px-6 py-6 space-y-1"
                    >
                        {mobileNavItems.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={n.tid}
                                className={({ isActive }) =>
                                    `block font-serif text-2xl py-2 ${
                                        isActive ? "text-terracotta" : "text-brown"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="nav-mobile-book"
                            onClick={() => trackContactCta("header-mobile")}
                            className="mt-4 inline-flex items-center gap-2 bg-brown text-creme rounded-full px-5 py-3 text-sm"
                        >
                            Prendre rendez-vous →
                        </Link>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
